import {useNotification} from '@hconnect/uikit/src/common'
import {useMutation} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useErrorHandler} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'

import {GET_SAMPLING_POINTS_PINNED_SECTION_KEY} from '../../api/queries/getSamplingPointsPinnedSection'

import {mutations} from '@settings/api/mutations'
import {setSettingsQueryData} from '@settings/modules/common/providers'
import {ErrorResponse} from '@settings/modules/common/types'
import {notifyIfErrorMessage} from '@settings/modules/common/utils'

type SaveSamplingPointsPinnedSectionParameters = Parameters<
  typeof mutations.saveSamplingPointsPinnedSection
>[0]

type SaveSamplingPointsPinnedSectionMutationParameters =
  SaveSamplingPointsPinnedSectionParameters & {
    samplingPointId?: number
    targetSection?: 'pinned' | 'lessUsed' | 'allSamplingPoints'
    shouldNotifySuccess?: boolean
  }

export function useSaveSamplingPointsPinnedSectionMutation() {
  const {notify} = useNotification()
  const raiseError = useErrorHandler()
  const {t} = useTranslation()

  return useMutation<
    void,
    AxiosError<ErrorResponse>,
    SaveSamplingPointsPinnedSectionMutationParameters
  >({
    mutationFn: ({keyIdentifier, value}) =>
      mutations.saveSamplingPointsPinnedSection({keyIdentifier, value}),
    onSuccess: (_data, variables) => {
      setSettingsQueryData(GET_SAMPLING_POINTS_PINNED_SECTION_KEY, [variables.keyIdentifier], {
        keyIdentifier: variables.keyIdentifier,
        value: variables.value
      })

      if (variables.shouldNotifySuccess === false) {
        return
      }

      const targetSectionLabel =
        variables.targetSection === 'pinned'
          ? t('samplingPointsSettings.pinnedRows')
          : variables.targetSection === 'lessUsed'
            ? t('samplingPointsSettings.lessUsedRows')
            : t('samplingPointsSettings.allSamplingPoints')

      notify(
        'success',
        t('samplingPointsSettings.moveSamplingPointToSectionSuccess', {
          samplingPointId: variables.samplingPointId,
          section: targetSectionLabel
        })
      )
    },
    onError: (error) => {
      const errorDetails = error?.response?.data.detail ?? error.message ?? error.response?.status
      notifyIfErrorMessage(
        t('samplingPointsSettings.mutations.savePinnedSamplingPointSectionError', {
          details: errorDetails
        }),
        raiseError,
        notify
      )
    }
  })
}
